import Panel from '../components/panel'
import { isNil, debounce } from 'lodash'
import onAnimationEnd from '@scripts/utils/onAnimationEnd'

class Common {
  searchContainer: HTMLElement | null
  searchToggle: HTMLElement | null
  searchInput: HTMLInputElement | null
  navSelect: HTMLElement | null
  header: HTMLElement | null

  constructor() {
    this.searchContainer = document.getElementById('prp-search-container')
    this.searchToggle = document.getElementById('prp-search-toggle')
    this.searchInput = document.getElementById('search-input') as HTMLInputElement
    this.navSelect = document.getElementById('prp-nav-select')
    this.header = document.getElementById('prp-header')

    // listeners
    this.searchToggle?.addEventListener('click', this.toggleSearch)
    this.navSelect?.addEventListener('change', this.onNavSelectChange)

    // enable panels
    new Panel()

    // sticky header via IntersectionObserver (no scroll polling)
    this.setupStickyObserver()
  }

  private setupStickyObserver = (): void => {
    if (isNil(this.header)) return

    // Fallback for browsers without IntersectionObserver
    if (!("IntersectionObserver" in window)) {
      this.setupStickyFallback()
      return
    }

    const sentinel = document.createElement('div')
    sentinel.setAttribute('data-sticky-sentinel', 'true')
    sentinel.style.width = '1px'
    sentinel.style.height = '1px'
    sentinel.style.position = 'relative'
    sentinel.style.pointerEvents = 'none'
    sentinel.style.opacity = '0'
    this.header.insertAdjacentElement('afterend', sentinel)

    const io = new IntersectionObserver(([entry]) => {
      if (isNil(this.header)) return
      if (entry.isIntersecting) {
        this.header.classList.remove('sticky', 'top-0', 'animate-slideInDown')
      } else {
        this.header.classList.add('sticky', 'top-0', 'animate-slideInDown')
      }
    })

    io.observe(sentinel)
  }

  private setupStickyFallback = (): void => {
    if (isNil(this.header)) return
    let threshold = this.header.offsetHeight

    const apply = (): void => {
      if (isNil(this.header)) return
      if (window.scrollY > threshold) {
        this.header.classList.add('sticky', 'top-0', 'animate-slideInDown')
      } else {
        this.header.classList.remove('sticky', 'top-0', 'animate-slideInDown')
      }
    }

    window.addEventListener('scroll', apply, { passive: true })
    const onResize = debounce(() => {
      if (isNil(this.header)) return
      threshold = this.header.offsetHeight
      apply()
    }, 200)
    window.addEventListener('resize', onResize)

    // initialize state
    apply()
  }

  public toggleSearch = (): void => {
    // a11y: sync toggle and container aria states
    const expanded = this.searchToggle?.getAttribute('aria-expanded') === 'true'
    const nowExpanded = !expanded
    this.searchToggle?.setAttribute('aria-expanded', nowExpanded ? 'true' : 'false')
    this.searchContainer?.setAttribute('aria-hidden', nowExpanded ? 'false' : 'true')

    // manage focusability of input while hidden
    if (!isNil(this.searchInput)) {
      if (nowExpanded) {
        this.searchInput.removeAttribute('disabled')
        this.searchInput.removeAttribute('aria-disabled')
        this.searchInput.removeAttribute('tabindex')
      } else {
        this.searchInput.setAttribute('disabled', 'true')
        this.searchInput.setAttribute('aria-disabled', 'true')
        this.searchInput.setAttribute('tabindex', '-1')
      }
    }

    if (!isNil(this.searchContainer) && this.searchContainer.classList.contains('translate-x-full')) {
      this.searchContainer.classList.remove('translate-x-full', 'animate-slideOutRight')
      if (!isNil(this.searchInput)) {
        onAnimationEnd(this.searchContainer, 'animate-slideInRight').then(() => {
          setTimeout(() => {
            this.searchInput?.focus()
          }, 300)
        })
      }
      return
    }

    this.searchContainer?.classList.remove('translate-x-0', 'animate-slideInRight')
    if (!isNil(this.searchInput)) {
      onAnimationEnd(this.searchContainer, 'animate-slideOutRight').then(() => {
        this.searchInput?.blur()
        this.searchContainer.classList.add('translate-x-full')
      })
    }
  }

  public onNavSelectChange = (event: Event): void => {
    const selectedValue = (event.target as HTMLSelectElement).value
    window.location.href = selectedValue
  }
}

export default Common
